import { type ModelConfig, type Prompt } from '@meridian-ssa/shared';
import { useEffect, useState } from 'react';

import { Modal } from '@/components/ui/Modal';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

import { CSVImporter } from './CSVImporter';
import { useExecutionSSE } from './hooks/useExecutionSSE';
import { PromptSetManager } from './PromptSetManager';
import { ConfigurationStep } from './steps/ConfigurationStep';
import { RunningStep } from './steps/RunningStep';
import { SelectionStep } from './steps/SelectionStep';

type WizardStep = 'select' | 'configure' | 'running';

export function ExecutionWizard({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (runId: string) => void;
}) {
  // Step management
  const [step, setStep] = useState<WizardStep>('select');

  // Data loading
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [configs, setConfigs] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [selectedPrompts, setSelectedPrompts] = useState<string[]>([]);
  const [selectedConfigs, setSelectedConfigs] = useState<string[]>([]);
  const [selectedPromptSetId, setSelectedPromptSetId] = useState<string | null>(
    null
  );

  // Configuration state
  const [runName, setRunName] = useState('');
  const [description, setDescription] = useState('');
  const [concurrency, setConcurrency] = useState(3);
  const [multiplicity, setMultiplicity] = useState(1);

  // Modals
  const [showCSVImporter, setShowCSVImporter] = useState(false);
  const [showPromptSetManager, setShowPromptSetManager] = useState(false);

  // SSE execution
  const { executionProgress, execute } = useExecutionSSE(onSuccess);

  useEffect(() => {
    loadData();
  }, []);

  // Data fetching
  async function loadData() {
    try {
      const [promptsData, configsData] = await Promise.all([
        apiRequest('/api/eval/prompts'),
        apiRequest('/api/eval/configs'),
      ]);

      setPrompts(promptsData.prompts || []);
      setConfigs(configsData.configs || []);
    } catch (e) {
      logError('Error loading wizard data', e);
    } finally {
      setLoading(false);
    }
  }

  // Selection handlers
  function togglePrompt(id: string) {
    setSelectedPrompts(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
    if (selectedPromptSetId) {
      setSelectedPromptSetId(null);
    }
  }

  function handleConfigSelectionChange(configIds: string[]) {
    setSelectedConfigs(configIds);
  }

  function handleSelectPromptSet(setId: string, promptIds: string[]) {
    setSelectedPrompts(promptIds);
    setSelectedPromptSetId(setId);
  }

  // Run name generation
  function generateRunName() {
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
    const promptCount = selectedPrompts.length;
    const configCount = selectedConfigs.length;
    return `Run ${timestamp} (${promptCount}p × ${configCount}c)`;
  }

  // Execute handler
  async function handleExecute() {
    if (selectedPrompts.length === 0 || selectedConfigs.length === 0) {
      return;
    }

    setStep('running');

    const finalRunName = runName.trim() || generateRunName();

    await execute({
      promptIds: selectedPrompts,
      configIds: selectedConfigs,
      runName: finalRunName,
      description: description.trim() || undefined,
      multiplicity,
      concurrency,
      promptSetId: selectedPromptSetId,
    });
  }

  // Computed values
  const totalExecutions =
    selectedPrompts.length * selectedConfigs.length * multiplicity;
  const isSubmitting =
    executionProgress.state === 'creating' ||
    executionProgress.state === 'running';

  // Step titles
  const stepTitles: Record<WizardStep, string> = {
    select: 'New Evaluation Run',
    configure: 'Configure Run',
    running: 'Execution Started',
  };

  return (
    <>
      <Modal
        title={stepTitles[step]}
        onClose={step !== 'running' ? onClose : () => {}}
        maxWidth="800px"
        showClose={step !== 'running'}
      >
        {loading ? (
          <div className="text-center py-8" style={{ color: '#A0A0A0' }}>
            Loading...
          </div>
        ) : step === 'select' ? (
          <SelectionStep
            prompts={prompts}
            configs={configs}
            selectedPrompts={selectedPrompts}
            selectedConfigs={selectedConfigs}
            selectedPromptSetId={selectedPromptSetId}
            onTogglePrompt={togglePrompt}
            onConfigSelectionChange={handleConfigSelectionChange}
            onShowCSVImporter={() => setShowCSVImporter(true)}
            onShowPromptSetManager={() => setShowPromptSetManager(true)}
            onNext={() => setStep('configure')}
            onCancel={onClose}
            multiplicity={multiplicity}
          />
        ) : step === 'configure' ? (
          <ConfigurationStep
            runName={runName}
            description={description}
            concurrency={concurrency}
            multiplicity={multiplicity}
            totalExecutions={totalExecutions}
            placeholderName={generateRunName()}
            onRunNameChange={setRunName}
            onDescriptionChange={setDescription}
            onConcurrencyChange={setConcurrency}
            onMultiplicityChange={setMultiplicity}
            onBack={() => setStep('select')}
            onExecute={handleExecute}
            isSubmitting={isSubmitting}
          />
        ) : (
          <RunningStep
            executionProgress={executionProgress}
            onClose={onClose}
            onBackToConfig={() => setStep('configure')}
          />
        )}
      </Modal>

      {/* Modals */}
      {showCSVImporter && (
        <CSVImporter
          onClose={() => setShowCSVImporter(false)}
          onSuccess={() => {
            setShowCSVImporter(false);
            loadData();
          }}
        />
      )}

      {showPromptSetManager && (
        <PromptSetManager
          onClose={() => setShowPromptSetManager(false)}
          onSelectSet={handleSelectPromptSet}
        />
      )}
    </>
  );
}
